import { useCreateFieldGetter } from '@/common/customisation/composables';
import { PUI } from '@pleased-npm/fe-customisation';

const loginFormBlockParams = {
  registryId: PUI.REGISTRY_IDS.LOGIN_PAGE,
  blockKey: PUI.LOGIN_BLOCK_KEYS.LOGIN_FORM,
} as const;

/**
 * Composable for login form body background customisation
 */
export const useLoginFormBodyBackground = () => {
  const { getField, isVisible } = useCreateFieldGetter({
    ...loginFormBlockParams,
    sectionKey: PUI.LOGIN_SECTION_KEYS.BODY_BACKGROUND,
  });

  const backgroundColor = getField(PUI.COMMON_FIELD_KEYS.BACKGROUND_COLOR);

  return {
    isVisible,
    backgroundColor,
  };
};

/**
 * Composable for login form title customisation
 */
export const useLoginFormTitle = () => {
  const { getField } = useCreateFieldGetter({
    ...loginFormBlockParams,
    sectionKey: PUI.LOGIN_SECTION_KEYS.TITLE,
  });

  const titleText = getField(PUI.COMMON_FIELD_KEYS.TEXT_CONTENT);
  const titleTextColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);

  return {
    titleText,
    titleTextColor,
  };
};

/**
 * Composable for login API error banner (title row). Message copy comes from the app/API, not CMS.
 */
export const useLoginFormApiErrorBanner = () => {
  const { getField, isVisible } = useCreateFieldGetter({
    ...loginFormBlockParams,
    sectionKey: PUI.LOGIN_SECTION_KEYS.API_ERROR_BANNER,
  });

  const backgroundColor = getField(PUI.COMMON_FIELD_KEYS.BACKGROUND_COLOR);
  const borderColor = getField(PUI.COMMON_FIELD_KEYS.BORDER_COLOR);
  const textColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);

  return {
    isVisible,
    backgroundColor,
    borderColor,
    textColor,
  };
};

/**
 * Composable for login form identifier (email / phone) input customisation
 */
export const useLoginFormIdentifierInput = () => {
  const { getField } = useCreateFieldGetter({
    ...loginFormBlockParams,
    sectionKey: PUI.LOGIN_SECTION_KEYS.IDENTIFIER_INPUT,
  });

  const placeholder = getField(PUI.COMMON_FIELD_KEYS.PLACEHOLDER_TEXT);
  const textColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);
  const placeholderColor = getField(PUI.COMMON_FIELD_KEYS.PLACEHOLDER_COLOR);
  const backgroundColor = getField(PUI.COMMON_FIELD_KEYS.BACKGROUND_COLOR);
  const borderColor = getField(PUI.COMMON_FIELD_KEYS.BORDER_COLOR);
  const errorBorderColor = getField(PUI.COMMON_FIELD_KEYS.ERROR_BORDER_COLOR);

  return {
    placeholder,
    textColor,
    placeholderColor,
    backgroundColor,
    borderColor,
    errorBorderColor,
  };
};

/**
 * Composable for login form password input customisation
 */
export const useLoginFormPasswordInput = () => {
  const { getField } = useCreateFieldGetter({
    ...loginFormBlockParams,
    sectionKey: PUI.LOGIN_SECTION_KEYS.PASSWORD_INPUT,
  });

  const placeholder = getField(PUI.COMMON_FIELD_KEYS.PLACEHOLDER_TEXT);
  const textColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);
  const placeholderColor = getField(PUI.COMMON_FIELD_KEYS.PLACEHOLDER_COLOR);
  const backgroundColor = getField(PUI.COMMON_FIELD_KEYS.BACKGROUND_COLOR);
  const borderColor = getField(PUI.COMMON_FIELD_KEYS.BORDER_COLOR);
  const errorBorderColor = getField(PUI.COMMON_FIELD_KEYS.ERROR_BORDER_COLOR);

  return {
    placeholder,
    textColor,
    placeholderColor,
    backgroundColor,
    borderColor,
    errorBorderColor,
  };
};

/**
 * Composable for login form submit button customisation
 */
export const useLoginFormSubmitButton = () => {
  const { getField, isVisible } = useCreateFieldGetter({
    ...loginFormBlockParams,
    blockKey: PUI.LOGIN_BLOCK_KEYS.LOGIN_ACTIONS,
    sectionKey: PUI.LOGIN_SECTION_KEYS.SUBMIT_BUTTON,
  });

  const buttonText = getField(PUI.COMMON_FIELD_KEYS.BUTTON_TEXT);
  const buttonBackgroundColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_BACKGROUND_COLOR);
  const buttonBorderColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_BORDER_COLOR);
  const buttonTextColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_TEXT_COLOR);
  const disabledButtonBackgroundColor = getField(PUI.COMMON_FIELD_KEYS.DISABLED_BUTTON_BACKGROUND_COLOR);
  const disabledButtonBorderColor = getField(PUI.COMMON_FIELD_KEYS.DISABLED_BUTTON_BORDER_COLOR);
  const disabledButtonTextColor = getField(PUI.COMMON_FIELD_KEYS.DISABLED_BUTTON_TEXT_COLOR);

  return {
    isVisible,
    buttonText,
    buttonBackgroundColor,
    buttonBorderColor,
    buttonTextColor,
    disabledButtonBackgroundColor,
    disabledButtonBorderColor,
    disabledButtonTextColor,
  };
};

/**
 * Composable for continue-as-guest action
 */
export const useLoginFormGuestButton = () => {
  const { getField, isVisible } = useCreateFieldGetter({
    ...loginFormBlockParams,
    blockKey: PUI.LOGIN_BLOCK_KEYS.LOGIN_ACTIONS,
    sectionKey: PUI.LOGIN_SECTION_KEYS.GUEST_BUTTON,
  });

  const buttonText = getField(PUI.COMMON_FIELD_KEYS.BUTTON_TEXT);
  const buttonBackgroundColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_BACKGROUND_COLOR);
  const buttonBorderColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_BORDER_COLOR);
  const buttonTextColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_TEXT_COLOR);
  const disabledButtonBackgroundColor = getField(PUI.COMMON_FIELD_KEYS.DISABLED_BUTTON_BACKGROUND_COLOR);
  const disabledButtonBorderColor = getField(PUI.COMMON_FIELD_KEYS.DISABLED_BUTTON_BORDER_COLOR);
  const disabledButtonTextColor = getField(PUI.COMMON_FIELD_KEYS.DISABLED_BUTTON_TEXT_COLOR);

  return {
    isVisible,
    buttonText,
    buttonBackgroundColor,
    buttonBorderColor,
    buttonTextColor,
    disabledButtonBackgroundColor,
    disabledButtonBorderColor,
    disabledButtonTextColor,
  };
};

/**
 * Cancel connect / support request — `login-actions` block, `cancel-button` section (CMS).
 */
export const useLoginFormCancelButton = () => {
  const { getField, isVisible } = useCreateFieldGetter({
    registryId: PUI.REGISTRY_IDS.LOGIN_PAGE,
    blockKey: PUI.LOGIN_BLOCK_KEYS.LOGIN_ACTIONS,
    sectionKey: 'cancel-button',
  });

  const buttonText = getField(PUI.COMMON_FIELD_KEYS.BUTTON_TEXT);
  const buttonBackgroundColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_BACKGROUND_COLOR);
  const buttonBorderColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_BORDER_COLOR);
  const buttonTextColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_TEXT_COLOR);
  const disabledButtonBackgroundColor = getField(PUI.COMMON_FIELD_KEYS.DISABLED_BUTTON_BACKGROUND_COLOR);
  const disabledButtonBorderColor = getField(PUI.COMMON_FIELD_KEYS.DISABLED_BUTTON_BORDER_COLOR);
  const disabledButtonTextColor = getField(PUI.COMMON_FIELD_KEYS.DISABLED_BUTTON_TEXT_COLOR);

  return {
    isVisible,
    buttonText,
    buttonBackgroundColor,
    buttonBorderColor,
    buttonTextColor,
    disabledButtonBackgroundColor,
    disabledButtonBorderColor,
    disabledButtonTextColor,
  };
};

/**
 * Combined composable that returns all login form customisations
 */
export const useLoginFormAllCustomisation = () => {
  const bodyBackground = useLoginFormBodyBackground();
  const title = useLoginFormTitle();
  const apiErrorBanner = useLoginFormApiErrorBanner();
  const identifierInput = useLoginFormIdentifierInput();
  const passwordInput = useLoginFormPasswordInput();
  const submitButton = useLoginFormSubmitButton();
  const guestButton = useLoginFormGuestButton();
  const cancelButton = useLoginFormCancelButton();

  return {
    bodyBackground,
    title,
    apiErrorBanner,
    identifierInput,
    passwordInput,
    submitButton,
    guestButton,
    cancelButton,
  };
};
